import copy from "copy-to-clipboard";
import {
  APK_TRANSFER_HASH_KEY,
  createApkTransferClipboardText,
  decryptApkTransferPayload,
} from "./apkTransferCodec";
import type { ApkTransferPayload } from "./apkTransferCodec";
import { persistRestoredPidForSession } from "../attribution/restoredPidSession";
import { getEmRuntimeContext } from "../analytics/em/context";
import type {
  OpenInstallLinkTrackOptions,
  PendingEmTrackEvent,
} from "../analytics/em/events";
import { trackEmEvent } from "../analytics/em/sdk";
import type { AttributionWindowLike } from "../attribution/runtime";
import {
  getResolvedPid,
  setRestoredPid,
} from "../attribution/runtime";

export * from "./apkTransferCodec";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface HistoryLike {
  replaceState(data: any, unused: string, url?: string | URL | null): void;
}

interface LocationLike {
  href: string;
  hash: string;
  search: string;
  pathname: string;
}

interface RestoreWindowLike extends AttributionWindowLike {
  location: LocationLike;
  history: HistoryLike;
  localStorage: StorageLike;
  sessionStorage?: StorageLike;
  setSysToken?: (token: string) => void;
}

interface ClipboardDeps {
  fallbackCopy?: (text: string) => boolean;
}

let pendingApkRestoreEvent: PendingEmTrackEvent | null = null;

const getLocationHost = (hrefOrHost: string): string => {
  if (!hrefOrHost) {
    return "";
  }

  try {
    return new URL(hrefOrHost).host;
  } catch (_error) {
    return hrefOrHost;
  }
};

const queuePendingApkRestoreEvent = (event: PendingEmTrackEvent): void => {
  pendingApkRestoreEvent = event;
};

const buildApkTrackProps = (
  installLink: string,
  options: OpenInstallLinkTrackOptions = {},
): Record<string, string | boolean> => {
  const context = getEmRuntimeContext();

  return {
    source: options.source || "",
    installLinkHost: getLocationHost(installLink),
    currentHost: context.currentHost,
    currentPath: context.currentPath,
    pidBefore: context.pid,
    tokenAttached: context.isLogin,
    isLogin: context.isLogin,
    countryCode: context.countryCode,
    clientEnvKind: context.clientEnvKind,
    isFacebookFamilyIAB: context.isFacebookFamilyIAB,
    fromRegisterFlow: !!options.fromRegisterFlow,
  };
};

const getCleanUrl = (location: LocationLike): string => {
  return `${location.pathname || "/"}${location.search || ""}`;
};

export const copyTextToClipboard = (
  text: string,
  deps: ClipboardDeps = {},
): boolean => {
  try {
    const fallbackCopy = deps.fallbackCopy ?? copy;
    return fallbackCopy(text);
  } catch (_error) {
    return false;
  }
};

export const restoreApkTransferFromUrl = (
  win: RestoreWindowLike = {
    location: window.location,
    history: window.history,
    localStorage: window.localStorage,
    sessionStorage: window.sessionStorage,
    __BOOTSTRAP_ATTR__: window.__BOOTSTRAP_ATTR__,
    __RUNTIME_ATTR__: window.__RUNTIME_ATTR__,
  },
): ApkTransferPayload | null => {
  const globalWindow =
    typeof window === "undefined" ? undefined : (window as RestoreWindowLike);
  const restoreTarget: RestoreWindowLike = {
    ...win,
    __BOOTSTRAP_ATTR__: win.__BOOTSTRAP_ATTR__ ?? globalWindow?.__BOOTSTRAP_ATTR__,
    __RUNTIME_ATTR__: win.__RUNTIME_ATTR__ ?? globalWindow?.__RUNTIME_ATTR__,
  };
  const hashValue = (restoreTarget.location.hash || "").replace(/^#/, "");
  if (!hashValue) {
    return null;
  }

  const hashParams = new URLSearchParams(hashValue);
  const cipherText = hashParams.get(APK_TRANSFER_HASH_KEY);
  if (!cipherText) {
    return null;
  }

  const pidBefore = getResolvedPid(restoreTarget);
  const payload = decryptApkTransferPayload(cipherText);
  if (!payload) {
    queuePendingApkRestoreEvent({
      eventName: "apk_restore_fail",
      props: {
        pidBefore,
        pidAfter: "",
        tokenAttached: false,
        reason: "invalid_payload",
      },
    });
    restoreTarget.history.replaceState(null, "", getCleanUrl(restoreTarget.location));
    return null;
  }

  if (payload?.pid) {
    setRestoredPid(payload.pid, restoreTarget);
    persistRestoredPidForSession(payload.pid, payload.ts, restoreTarget);
  }
  if (payload?.token) {
    restoreTarget.localStorage.setItem("authToken", payload.token);
    restoreTarget.setSysToken?.(payload.token);
  }

  queuePendingApkRestoreEvent({
    eventName: "apk_restore_success",
    props: {
      pidBefore,
      pidAfter: payload.pid,
      tokenAttached: !!payload.token,
      source: payload.source || "",
    },
  });
  restoreTarget.history.replaceState(null, "", getCleanUrl(restoreTarget.location));
  return payload;
};

export const consumePendingApkRestoreEvent = (): PendingEmTrackEvent | null => {
  const event = pendingApkRestoreEvent;
  pendingApkRestoreEvent = null;
  return event;
};

export const openInstallLinkWithApkTransfer = (
  installLink: string,
  options: OpenInstallLinkTrackOptions = {},
): void => {
  const trackProps = buildApkTrackProps(installLink, options);
  trackEmEvent("apk_download_click", trackProps);

  try {
    const pid = getResolvedPid();
    const token = localStorage.getItem("authToken") || undefined;
    if (!pid) {
      console.warn("Failed to prepare APK transfer payload: missing resolved pid");
      trackEmEvent("apk_transfer_payload_fail", {
        ...trackProps,
        reason: "missing_pid",
      });
    } else {
      const text = createApkTransferClipboardText(
        pid,
        token,
        Date.now(),
        options.source,
      );
      const copied = copyTextToClipboard(text);
      if (copied) {
        trackEmEvent("apk_transfer_payload_ready", trackProps);
      } else {
        trackEmEvent("apk_transfer_payload_fail", {
          ...trackProps,
          reason: "copy_failed",
        });
      }
    }
  } catch (error) {
    console.warn("Failed to prepare APK transfer payload", error);
    trackEmEvent("apk_transfer_payload_fail", {
      ...trackProps,
      reason: "exception",
    });
  }

  trackEmEvent("apk_download_redirect", trackProps);
  window.location.href = installLink;
};
